import { existsSync, readFileSync, writeFileSync } from 'node:fs'
import { writeSecretFileSync } from './secure-file'
import { join, resolve } from 'node:path'
import { resolveAppNameForEnv, readWranglerConfig } from './wrangler-env'
import { parseDevVars } from '../env'
import { RESERVED_BINDING_NAMES } from '../../server/rooms/binding-manifest'

export const DEEPSPACE_SECRETS_PROJECT_VAR = 'DEEPSPACE_SECRETS_PROJECT'
export const DEEPSPACE_SECRETS_CONFIG_VAR = 'DEEPSPACE_SECRETS_CONFIG'

export const GENERATED_SECRETS_DIVIDER =
  '# --- DeepSpace secrets cache: generated by `npx deepspace secrets`; do not edit manually ---'

export const REQUIRED_SECRETS_BEGIN =
  '# --- DeepSpace detected secrets (names only; values live in `npx deepspace secrets`) ---'
export const REQUIRED_SECRETS_END = '# --- end DeepSpace detected secrets ---'

export const SECRET_NAME_RE = /^[A-Za-z_][A-Za-z0-9_]*$/
export const PROJECT_NAME_RE = /^[a-z0-9][a-z0-9-]{0,62}$/
export const CONFIG_NAME_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/

const MAX_SECRET_VALUE_BYTES = 32 * 1024

export interface SecretsTarget {
  projectName: string
  configName: string
  wranglerEnv?: string
}

export interface SecretsApiOptions {
  appName?: string
}

export interface LinkedSecretsTarget extends SecretsTarget {
  linked: true
}

export interface UnlinkedSecretsTarget {
  linked: false
  defaultProjectName: string
  defaultConfigName: string
  wranglerEnv?: string
}

export type SecretsLinkResolution = LinkedSecretsTarget | UnlinkedSecretsTarget

export interface SecretsCacheSource extends SecretsTarget {
  pulledAt?: string
}

export interface SecretListItem {
  key: string
  masked: string
  version: number
  updatedAt: string
  updatedBy?: string
}

export interface SecretsConfigInfo {
  name: string
  createdAt: string
  updatedAt: string
  secretCount?: number
}

export interface CreateSecretsConfigResult {
  ok: true
  created: boolean
  copiedFrom?: string
  copiedSecrets?: number
}

export interface PulledSecretsCache extends SecretsTarget {
  linked: true
  values: Record<string, string>
}

export type SecretsDownloadFormat =
  | 'json'
  | 'dotnet-json'
  | 'env'
  | 'yaml'
  | 'docker'
  | 'env-no-quotes'

export function validateProjectName(projectName: string): string {
  const trimmed = projectName.trim()
  if (!PROJECT_NAME_RE.test(trimmed)) {
    throw new Error(
      `Invalid secrets project "${projectName}". Use lowercase letters, numbers, and hyphens; start with a letter or number.`,
    )
  }
  return trimmed
}

export function validateConfigName(configName: string): string {
  const trimmed = configName.trim()
  if (!CONFIG_NAME_RE.test(trimmed)) {
    throw new Error(
      `Invalid secrets config "${configName}". Use letters, numbers, underscores, or hyphens; start with a letter or number.`,
    )
  }
  return trimmed
}

export function validateSecretName(name: string): string {
  const trimmed = name.trim()
  if (!SECRET_NAME_RE.test(trimmed)) {
    throw new Error(
      `Invalid secret name "${name}". Secret names must use letters, numbers, and underscores, and may not start with a number.`,
    )
  }
  if (RESERVED_BINDING_NAMES.has(trimmed)) {
    throw new Error(`"${trimmed}" is reserved by DeepSpace and cannot be used as an app secret.`)
  }
  return trimmed
}

export function validateSecretValue(key: string, value: string): void {
  const bytes = new TextEncoder().encode(value).byteLength
  if (bytes > MAX_SECRET_VALUE_BYTES) {
    throw new Error(`Secret "${key}" exceeds ${MAX_SECRET_VALUE_BYTES} bytes.`)
  }
}

export function defaultConfigNameForEnv(wranglerEnv?: string): string {
  return wranglerEnv ? validateConfigName(wranglerEnv) : 'prd'
}

export function readTopLevelAppName(appDir: string): string {
  const cfg = readWranglerConfig(appDir)
  const resolved = resolveAppNameForEnv(cfg, undefined)
  if (!resolved.ok) throw new Error(resolved.reason)
  if (cfg.name !== resolved.name) {
    throw new Error(
      `wrangler.toml: \`name\` "${cfg.name}" is not in canonical form. ` +
        `Update it to "${resolved.name}" and re-run.`,
    )
  }
  return resolved.name
}

export function resolveSecretsAppName(appDir: string, wranglerEnv?: string): string {
  const cfg = readWranglerConfig(appDir)
  const resolved = resolveAppNameForEnv(cfg, wranglerEnv)
  if (!resolved.ok) throw new Error(resolved.reason)
  return resolved.name
}

function stringVar(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

export function resolveSecretsLink(appDir: string, wranglerEnv?: string): SecretsLinkResolution {
  const cfg = readWranglerConfig(appDir)
  const defaultProjectName = readTopLevelAppName(appDir)
  const defaultConfigName = defaultConfigNameForEnv(wranglerEnv)
  const topVars = cfg.vars ?? {}
  const envVars = wranglerEnv ? (cfg.env?.[wranglerEnv]?.vars ?? {}) : {}
  const projectName =
    stringVar(envVars[DEEPSPACE_SECRETS_PROJECT_VAR]) ??
    stringVar(topVars[DEEPSPACE_SECRETS_PROJECT_VAR])

  if (!projectName) {
    return { linked: false, defaultProjectName, defaultConfigName, wranglerEnv }
  }

  const configName =
    stringVar(envVars[DEEPSPACE_SECRETS_CONFIG_VAR]) ??
    (wranglerEnv ? defaultConfigName : (stringVar(topVars[DEEPSPACE_SECRETS_CONFIG_VAR]) ?? 'prd'))

  return {
    linked: true,
    projectName: validateProjectName(projectName),
    configName: validateConfigName(configName),
    wranglerEnv,
  }
}

export function resolveSecretsTarget(
  appDir: string,
  opts: { project?: string; config?: string; wranglerEnv?: string },
): SecretsTarget {
  const link = resolveSecretsLink(appDir, opts.wranglerEnv)
  const projectName = validateProjectName(
    opts.project ?? (link.linked ? link.projectName : link.defaultProjectName),
  )
  const configName = validateConfigName(
    opts.config ?? (link.linked ? link.configName : link.defaultConfigName),
  )
  return { projectName, configName, wranglerEnv: opts.wranglerEnv }
}

export function renderSecretsCache(
  secrets: Record<string, string>,
  source: SecretsCacheSource,
): string {
  const keys = Object.keys(secrets).sort()
  const lines = [
    GENERATED_SECRETS_DIVIDER,
    `# Source: project=${source.projectName} config=${source.configName}`,
    `# Pulled: ${source.pulledAt ?? new Date().toISOString()}`,
    '# Use `npx deepspace secrets` to change these values.',
  ]
  for (const key of keys) {
    validateSecretName(key)
    validateSecretValue(key, secrets[key])
    lines.push(`${key}=${quoteDotenvValue(secrets[key])}`)
  }
  return lines.join('\n')
}

export function stripGeneratedSecretsCache(content: string): string {
  const idx = content.indexOf(GENERATED_SECRETS_DIVIDER)
  if (idx < 0) return content.replace(/\s+$/u, '')
  return content.slice(0, idx).replace(/\s+$/u, '')
}

export function formatSecretsDownload(
  secrets: Record<string, string>,
  format: SecretsDownloadFormat,
): string {
  const entries = Object.entries(secrets).sort(([a], [b]) => a.localeCompare(b))
  switch (format) {
    case 'json':
    case 'dotnet-json':
      return JSON.stringify(Object.fromEntries(entries), null, 2) + '\n'
    case 'env':
      return entries.map(([key, value]) => `${key}=${quoteDotenvValue(value)}`).join('\n') + '\n'
    case 'env-no-quotes':
    case 'docker':
      return entries.map(([key, value]) => `${key}=${value}`).join('\n') + '\n'
    case 'yaml':
      return entries.map(([key, value]) => `${key}: ${JSON.stringify(value)}`).join('\n') + '\n'
  }
}

export function writeSecretsCacheFile(
  appDir: string,
  secrets: Record<string, string>,
  source: SecretsCacheSource,
): void {
  const devVarsPath = secretsCacheFilePath(appDir)
  const existing = existsSync(devVarsPath) ? readFileSync(devVarsPath, 'utf-8') : ''
  const head = stripGeneratedSecretsCache(existing)
  const rendered = renderSecretsCache(secrets, source)
  writeSecretFileSync(devVarsPath, head ? `${head}\n\n${rendered}\n` : `${rendered}\n`)
}

export function clearSecretsCacheFile(appDir: string): void {
  const devVarsPath = secretsCacheFilePath(appDir)
  if (!existsSync(devVarsPath)) return
  const existing = readFileSync(devVarsPath, 'utf-8')
  const next = stripGeneratedSecretsCache(existing)
  writeSecretFileSync(devVarsPath, next ? `${next}\n` : '')
}

export function secretsTargetMatchesLink(appDir: string, target: SecretsTarget): boolean {
  const link = resolveSecretsLink(appDir, target.wranglerEnv)
  return (
    link.linked && link.projectName === target.projectName && link.configName === target.configName
  )
}

export function appScopedSecretsApiOptions(
  appDir: string,
  target: Pick<SecretsTarget, 'projectName' | 'wranglerEnv'>,
): SecretsApiOptions {
  const link = resolveSecretsLink(appDir, target.wranglerEnv)
  if (!link.linked || link.projectName !== target.projectName) return {}
  return { appName: resolveSecretsAppName(appDir, target.wranglerEnv) }
}

function secretsCacheFilePath(appDir: string): string {
  return join(appDir, '.dev.vars')
}

export async function pullLinkedSecretsCache(
  deployUrl: string,
  token: string,
  appDir: string,
  wranglerEnv?: string,
): Promise<PulledSecretsCache | null> {
  const link = resolveSecretsLink(appDir, wranglerEnv)
  if (!link.linked) return null
  const values = await fetchSecretsValues(deployUrl, token, link.projectName, link.configName, {
    appName: resolveSecretsAppName(appDir, wranglerEnv),
  })
  return {
    linked: true,
    projectName: link.projectName,
    configName: link.configName,
    wranglerEnv,
    values,
  }
}

export async function fetchSecretsValues(
  deployUrl: string,
  token: string,
  projectName: string,
  configName: string,
  options: SecretsApiOptions = {},
): Promise<Record<string, string>> {
  const body = await secretsApi<{ secrets: Record<string, string> }>(
    deployUrl,
    token,
    `/api/secrets/${encodeURIComponent(projectName)}/configs/${encodeURIComponent(configName)}/values`,
    {},
    options,
  )
  return body.secrets
}

export async function listSecrets(
  deployUrl: string,
  token: string,
  projectName: string,
  configName: string,
  options: SecretsApiOptions = {},
): Promise<{ projectName: string; configName: string; secrets: SecretListItem[] }> {
  return secretsApi(
    deployUrl,
    token,
    `/api/secrets/${encodeURIComponent(projectName)}/configs/${encodeURIComponent(configName)}`,
    {},
    options,
  )
}

export async function getSecretPlain(
  deployUrl: string,
  token: string,
  projectName: string,
  configName: string,
  key: string,
  options: SecretsApiOptions = {},
): Promise<string> {
  const body = await secretsApi<{ key: string; value: string }>(
    deployUrl,
    token,
    `/api/secrets/${encodeURIComponent(projectName)}/configs/${encodeURIComponent(configName)}/secrets/${encodeURIComponent(key)}?plain=true`,
    {},
    options,
  )
  return body.value
}

export async function deleteSecret(
  deployUrl: string,
  token: string,
  projectName: string,
  configName: string,
  key: string,
  options: SecretsApiOptions = {},
): Promise<void> {
  await secretsApi(
    deployUrl,
    token,
    `/api/secrets/${encodeURIComponent(projectName)}/configs/${encodeURIComponent(configName)}/secrets/${encodeURIComponent(key)}`,
    { method: 'DELETE' },
    options,
  )
}

export async function uploadSecrets(
  deployUrl: string,
  token: string,
  projectName: string,
  configName: string,
  secrets: Record<string, string>,
  replace: boolean,
  options: SecretsApiOptions = {},
): Promise<void> {
  await secretsApi(
    deployUrl,
    token,
    `/api/secrets/${encodeURIComponent(projectName)}/configs/${encodeURIComponent(configName)}/secrets`,
    {
      method: 'PUT',
      body: JSON.stringify({ secrets, replace }),
    },
    options,
  )
}

export async function listConfigs(
  deployUrl: string,
  token: string,
  projectName: string,
  options: SecretsApiOptions = {},
): Promise<SecretsConfigInfo[]> {
  const body = await secretsApi<{ configs: SecretsConfigInfo[] }>(
    deployUrl,
    token,
    `/api/secrets/${encodeURIComponent(projectName)}/configs`,
    {},
    options,
  )
  return body.configs
}

export async function createConfig(
  deployUrl: string,
  token: string,
  projectName: string,
  configName: string,
  copyFromConfigName?: string,
  options: SecretsApiOptions = {},
): Promise<CreateSecretsConfigResult> {
  return secretsApi(
    deployUrl,
    token,
    `/api/secrets/${encodeURIComponent(projectName)}/configs`,
    {
      method: 'POST',
      body: JSON.stringify({
        name: configName,
        ...(copyFromConfigName ? { copyFrom: copyFromConfigName } : {}),
      }),
    },
    options,
  )
}

export async function deleteConfig(
  deployUrl: string,
  token: string,
  projectName: string,
  configName: string,
  options: SecretsApiOptions = {},
): Promise<void> {
  await secretsApi(
    deployUrl,
    token,
    `/api/secrets/${encodeURIComponent(projectName)}/configs/${encodeURIComponent(configName)}`,
    { method: 'DELETE' },
    options,
  )
}

export async function setServerSecretsLink(
  deployUrl: string,
  token: string,
  appName: string,
  projectName: string,
): Promise<void> {
  await secretsApi(deployUrl, token, `/api/secrets-links/${encodeURIComponent(appName)}`, {
    method: 'PUT',
    body: JSON.stringify({ projectName }),
  })
}

export async function clearServerSecretsLink(
  deployUrl: string,
  token: string,
  appName: string,
): Promise<void> {
  await secretsApi(deployUrl, token, `/api/secrets-links/${encodeURIComponent(appName)}`, {
    method: 'DELETE',
  })
}

export function parseSecretsUpload(content: string): Record<string, string> {
  const trimmed = content.trim()
  if (!trimmed) return {}
  if (trimmed.startsWith('{')) {
    const parsed = JSON.parse(trimmed) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error('JSON upload must be an object of KEY=value strings.')
    }
    const out: Record<string, string> = {}
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      const name = validateSecretName(key)
      if (typeof value !== 'string') {
        throw new Error(`Secret "${name}" must be a string.`)
      }
      validateSecretValue(name, value)
      out[name] = value
    }
    return out
  }
  const parsed = parseDevVars(content)
  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(parsed)) {
    const name = validateSecretName(key)
    validateSecretValue(name, value)
    out[name] = value
  }
  return out
}

export function writeWranglerSecretsLink(
  appDir: string,
  target: SecretsTarget,
  opts: { projectOverride?: boolean } = {},
): void {
  const path = join(resolve(appDir), 'wrangler.toml')
  const raw = readFileSync(path, 'utf-8')
  const table = target.wranglerEnv ? `env.${target.wranglerEnv}.vars` : 'vars'
  const vars: Record<string, string | undefined> = {
    [DEEPSPACE_SECRETS_CONFIG_VAR]: target.configName,
  }
  if (!target.wranglerEnv || opts.projectOverride) {
    vars[DEEPSPACE_SECRETS_PROJECT_VAR] = target.projectName
  } else {
    const link = resolveSecretsLink(appDir, undefined)
    if (!link.linked) {
      vars[DEEPSPACE_SECRETS_PROJECT_VAR] = target.projectName
    }
  }
  writeFileSync(path, setTomlVars(raw, table, vars))
}

export function removeWranglerSecretsLink(appDir: string, wranglerEnv?: string): void {
  removeWranglerSecretsLinkKeys(appDir, wranglerEnv, ['project', 'config'])
}

export function removeWranglerSecretsLinkKeys(
  appDir: string,
  wranglerEnv: string | undefined,
  keys: Array<'project' | 'config'>,
): void {
  const path = join(resolve(appDir), 'wrangler.toml')
  const raw = readFileSync(path, 'utf-8')
  const table = wranglerEnv ? `env.${wranglerEnv}.vars` : 'vars'
  const vars: Record<string, string | undefined> = {}
  if (keys.includes('project')) vars[DEEPSPACE_SECRETS_PROJECT_VAR] = undefined
  if (keys.includes('config')) vars[DEEPSPACE_SECRETS_CONFIG_VAR] = undefined
  if (Object.keys(vars).length === 0) return
  writeFileSync(path, setTomlVars(raw, table, vars))
}

export function readWranglerRequiredSecretNames(appDir: string): string[] {
  const path = join(resolve(appDir), 'wrangler.toml')
  const raw = readFileSync(path, 'utf-8')
  return readRequiredSecretNamesFromToml(raw)
}

export function updateWranglerRequiredSecrets(
  appDir: string,
  secretNames: Iterable<string>,
  opts: { replace?: boolean } = {},
): string[] {
  const path = join(resolve(appDir), 'wrangler.toml')
  const raw = readFileSync(path, 'utf-8')
  const existing = readRequiredSecretNamesFromToml(raw)
  const incoming = normalizeRequiredSecretNames(secretNames)
  const next = opts.replace ? incoming : normalizeRequiredSecretNames([...existing, ...incoming])
  writeFileSync(
    path,
    setRequiredSecretNamesInToml(raw, next, existing.length > 0 || hasRequiredSecretsBlock(raw)),
  )
  return next
}

export function removeWranglerRequiredSecrets(
  appDir: string,
  secretNames: Iterable<string>,
): string[] {
  const path = join(resolve(appDir), 'wrangler.toml')
  const raw = readFileSync(path, 'utf-8')
  const remove = new Set(normalizeRequiredSecretNames(secretNames))
  const next = readRequiredSecretNamesFromToml(raw).filter((key) => !remove.has(key))
  writeFileSync(path, setRequiredSecretNamesInToml(raw, next, hasRequiredSecretsBlock(raw)))
  return next
}

function quoteDotenvValue(value: string): string {
  if (value === '') return '""'
  if (/^[^\s"'#=]+$/u.test(value)) return value
  return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`
}

export class SecretsApiError extends Error {
  readonly status: number
  readonly detail: string

  constructor(path: string, status: number, detail: string) {
    super(`Secrets API ${path} (${status}): ${detail}`)
    this.name = 'SecretsApiError'
    this.status = status
    this.detail = detail
  }
}

async function secretsApi<T>(
  deployUrl: string,
  token: string,
  path: string,
  init: RequestInit = {},
  options: SecretsApiOptions = {},
): Promise<T> {
  const scopedPath = withSecretsApiOptions(path, options)
  let res = await fetch(`${deployUrl}${scopedPath}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
  })
  let text = await res.text()
  const parsedError = parseSecretsApiError(text)
  if (!res.ok && res.status === 404 && parsedError === 'app_not_found' && options.appName) {
    res = await fetch(`${deployUrl}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(init.headers ?? {}),
      },
    })
    text = await res.text()
  }
  if (!res.ok) {
    let detail = text
    try {
      detail = (JSON.parse(text) as { error?: string }).error ?? text
    } catch {
      // keep text
    }
    throw new SecretsApiError(path, res.status, detail)
  }
  if (!text) return undefined as T
  return JSON.parse(text) as T
}

function withSecretsApiOptions(path: string, options: SecretsApiOptions): string {
  if (!options.appName) return path
  const sep = path.includes('?') ? '&' : '?'
  return `${path}${sep}appName=${encodeURIComponent(options.appName)}`
}

function parseSecretsApiError(text: string): string | null {
  try {
    const parsed = JSON.parse(text) as { error?: unknown }
    return typeof parsed.error === 'string' ? parsed.error : null
  } catch {
    return null
  }
}

function setTomlVars(raw: string, table: string, vars: Record<string, string | undefined>): string {
  const lines = raw.replace(/\r\n/g, '\n').split('\n')
  const header = `[${table}]`
  const headerRe = new RegExp(`^\\s*\\[${escapeRegExp(table)}\\]\\s*(?:#.*)?$`)
  let start = lines.findIndex((line) => headerRe.test(line))

  if (start < 0) {
    const additions = Object.entries(vars)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => `${key} = ${JSON.stringify(value)}`)
    const prefix = raw.trimEnd()
    return `${prefix}${prefix ? '\n\n' : ''}${header}\n${additions.join('\n')}\n`
  }

  let end = start + 1
  // Match both [table] and [[array-of-tables]] headers — missing the latter
  // makes the scan run past [[kv_namespaces]]-style blocks and splice the
  // link vars inside them, silently unlinking the app.
  while (end < lines.length && !/^\s*\[\[?[^\]]+\]\]?\s*(?:#.*)?$/u.test(lines[end])) end++

  const tableLines = lines.slice(start + 1, end)
  const seen = new Set<string>()
  const nextTableLines: string[] = []
  for (const line of tableLines) {
    const key = Object.keys(vars).find((name) =>
      new RegExp(`^\\s*${escapeRegExp(name)}\\s*=`).test(line),
    )
    if (!key) {
      nextTableLines.push(line)
      continue
    }
    seen.add(key)
    const value = vars[key]
    if (value !== undefined) nextTableLines.push(`${key} = ${JSON.stringify(value)}`)
  }
  for (const [key, value] of Object.entries(vars)) {
    if (!seen.has(key) && value !== undefined) {
      nextTableLines.push(`${key} = ${JSON.stringify(value)}`)
    }
  }

  lines.splice(start + 1, end - start - 1, ...nextTableLines)
  return lines.join('\n').replace(/\s*$/u, '\n')
}

function readRequiredSecretNamesFromToml(raw: string): string[] {
  const block = findRequiredSecretsBlock(raw)
  if (!block) return []
  const names: string[] = []
  for (const line of block.lines.slice(block.start + 1, block.end)) {
    const match = /^\s*#\s+([A-Za-z_][A-Za-z0-9_]*)\s*$/u.exec(line)
    if (match) names.push(validateSecretName(match[1]))
  }
  return normalizeRequiredSecretNames(names)
}

function setRequiredSecretNamesInToml(
  raw: string,
  names: string[],
  keepEmptyBlock: boolean,
): string {
  const normalized = raw.replace(/\r\n/g, '\n').replace(/\s*$/u, '\n')
  const block = findRequiredSecretsBlock(normalized)
  if (names.length === 0 && !block && !keepEmptyBlock) return normalized

  const rendered = renderRequiredSecretsBlock(names)
  if (block) {
    const lines = [...block.lines]
    lines.splice(block.start, block.end - block.start + 1, ...rendered)
    return lines.join('\n').replace(/\s*$/u, '\n')
  }

  const lines = normalized.split('\n')
  if (lines[lines.length - 1] === '') lines.pop()
  const insertAt = requiredSecretsInsertIndex(lines)
  const insertion =
    insertAt < lines.length ? [...rendered, ''] : lines.length > 0 ? ['', ...rendered] : rendered
  lines.splice(insertAt, 0, ...insertion)
  return lines.join('\n').replace(/\s*$/u, '\n')
}

function renderRequiredSecretsBlock(names: string[]): string[] {
  return [
    REQUIRED_SECRETS_BEGIN,
    ...(names.length > 0 ? names.map((name) => `#   ${name}`) : ['#   (none declared)']),
    REQUIRED_SECRETS_END,
  ]
}

function normalizeRequiredSecretNames(secretNames: Iterable<string>): string[] {
  return Array.from(new Set(Array.from(secretNames, (name) => validateSecretName(name)))).sort(
    (a, b) => a.localeCompare(b),
  )
}

function hasRequiredSecretsBlock(raw: string): boolean {
  return findRequiredSecretsBlock(raw) !== null
}

function findRequiredSecretsBlock(
  raw: string,
): { lines: string[]; start: number; end: number } | null {
  const lines = raw.replace(/\r\n/g, '\n').split('\n')
  const start = lines.findIndex((line) => requiredSecretsBeginRe().test(line))
  if (start < 0) return null
  const end = lines.findIndex((line, index) => index > start && requiredSecretsEndRe().test(line))
  return { lines, start, end: end < 0 ? start : end }
}

function requiredSecretsInsertIndex(lines: string[]): number {
  const varsHeader = /^\s*\[vars\]\s*(?:#.*)?$/u
  // [table] or [[array-of-tables]] — same reasoning as the setTomlVars scan.
  const tableHeader = /^\s*\[\[?[^\]]+\]\]?\s*(?:#.*)?$/u
  const varsStart = lines.findIndex((line) => varsHeader.test(line))
  if (varsStart < 0) return lines.length

  let end = varsStart + 1
  while (end < lines.length && !tableHeader.test(lines[end])) end++
  while (end > varsStart + 1 && lines[end - 1].trim() === '') end--
  return end
}

function requiredSecretsBeginRe(): RegExp {
  return /^# --- DeepSpace (?:detected|required) secrets\b.*---$/u
}

function requiredSecretsEndRe(): RegExp {
  return /^# --- end DeepSpace (?:detected|required) secrets ---$/u
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
